import {
  Component,
  createContext,
  createSignal,
  JSX,
  useContext,
  Accessor,
  Setter,
} from 'solid-js';
import { IComment, IUser } from './models/comment.types';

export interface CommentContextState {
  readonly currentUser: IUser | null;
  readonly selectedComment: IComment | null;
  readonly isPreviewMode: boolean;
  readonly editingCommentId: string | null;
  readonly projectId: string | null;
  readonly issueId: string | null;
  // Added for AttachmentPicker integration
  readonly accessToken: string | null;
  readonly tcpsApiDomain: string | null;
}

export interface CommentContextActions {
  setCurrentUser(user: IUser | null): void;
  setSelectedComment(comment: IComment | null): void;
  setPreviewMode(isPreview: boolean): void;
  setEditingCommentId(commentId: string | null): void;
  setProjectId(projectId: string | null): void;
  setIssueId(issueId: string | null): void;
  // Added for AttachmentPicker integration
  setAccessToken(accessToken: string | null): void;
  setTcpsApiDomain(tcpsApiDomain: string | null): void;
}

export interface CommentContextValue {
  readonly state: Accessor<CommentContextState>;
  readonly setState: Setter<CommentContextState>;
  readonly actions: CommentContextActions;
}

const CommentContext = createContext<CommentContextValue>();

export interface CommentContextProviderProps {
  readonly children?: JSX.Element;
  readonly initialState?: Partial<CommentContextState>;
}

const DEFAULT_STATE: CommentContextState = {
  currentUser: null,
  selectedComment: null,
  isPreviewMode: false,
  editingCommentId: null,
  projectId: null,
  issueId: null,
  accessToken: null,
  tcpsApiDomain: null,
};

export const CommentContextProvider: Component<CommentContextProviderProps> = (
  props: CommentContextProviderProps
) => {
  const [state, setState] = createSignal<CommentContextState>({
    ...DEFAULT_STATE,
    ...props.initialState,
  });

  const actions: CommentContextActions = {
    setCurrentUser: (user: IUser | null) => {
      setState((current) => ({ ...current, currentUser: user }));
    },
    setSelectedComment: (comment: IComment | null) => {
      setState((current) => ({ ...current, selectedComment: comment }));
    },
    setPreviewMode: (isPreview: boolean) => {
      setState((current) => ({ ...current, isPreviewMode: isPreview }));
    },
    setEditingCommentId: (commentId: string | null) => {
      setState((current) => ({ ...current, editingCommentId: commentId }));
    },
    setProjectId: (projectId: string | null) => {
      setState((current) => ({ ...current, projectId }));
    },
    setIssueId: (issueId: string | null) => {
      setState((current) => ({ ...current, issueId }));
    },
    setAccessToken: (accessToken: string | null) => {
      setState((current) => ({ ...current, accessToken }));
    },
    setTcpsApiDomain: (tcpsApiDomain: string | null) => {
      setState((current) => ({ ...current, tcpsApiDomain }));
    },
  };

  const contextValue: CommentContextValue = {
    state,
    setState,
    actions,
  };

  return (
    <CommentContext.Provider value={contextValue}>
      {props.children}
    </CommentContext.Provider>
  );
};

export const useCommentContext = (): CommentContextValue => {
  const context = useContext(CommentContext);
  if (!context) {
    throw new Error(
      'useCommentContext must be used within a CommentContextProvider'
    );
  }
  return context;
};

export const useCommentState = (): Accessor<CommentContextState> => {
  const { state } = useCommentContext();
  return state;
};

export const useCommentActions = (): CommentContextActions => {
  const { actions } = useCommentContext();
  return actions;
};
